/**
 * Annotation Types for TrafficDesigner
 * 
 * Page-based annotation system for the Annotation Editor.
 * Annotations are positioned using x/y pixel coordinates relative to the page,
 * not map lat/lng coordinates.
 */

export type AnnotationType = 
  | 'title-box'
  | 'north-arrow' 
  | 'legend' 
  | 'manifest' 
  | 'notes'
  | 'scale-bar';

/**
 * Page position - used for all annotations in the editor
 */
export interface PagePosition {
  x: number;  // pixels from left edge of page
  y: number;  // pixels from top edge of page
}

/**
 * Base annotation interface - all annotations extend this
 */
export interface BasePageAnnotation {
  id: string;
  type: AnnotationType;
  position: PagePosition;
  width: number;
  height: number;
  visible: boolean;
  locked?: boolean;
}

/**
 * Title Box - Uses existing TitleBoxData structure
 * This matches the existing TitleBoxDialog configuration exactly
 */
export interface TitleBoxAnnotation extends BasePageAnnotation {
  type: 'title-box';
  // Core toggle
  useTitleBox: boolean;
  // Export header
  exportTitle: string;
  // Project information
  tcpNumber: string;
  client: string;
  roadType: string;
  contact: string;
  phone: string;
  location: string;
  council: string;
  startDate: string;
  finishDate: string;
  startTime: string;
  finishTime: string;
  author: string;
  createdOn: string;
  modifiedBy: string;
  modifiedOn: string;
  // TGS Requirements
  state: string;
  lengthOfZone: string;
  roadSpeed: string;
  // Logos (base64 data URLs)
  logos: {
    left: string | null;
    center: string | null;
    right: string | null;
  };
  // Custom spacing overrides
  customSpacingRequirements?: Record<string, string>;
  // Visibility toggles
  showLogoLeft: boolean;
  showLogoCenter: boolean;
  showLogoRight: boolean;
  showMetadataSection: boolean;
  showSpacingTable: boolean;
}

/**
 * North Arrow - Shows map orientation
 */
export type NorthArrowStyle = 'star' | 'arrow' | 'compass' | 'simple';

export interface NorthArrowAnnotation extends BasePageAnnotation {
  type: 'north-arrow';
  rotation: number;
  style: NorthArrowStyle;
}

/**
 * Legend Box - Auto-populated list of plan elements
 */
export interface LegendEntry {
  icon: string;
  label: string;
  code?: string;
  count: number;
  category?: string;
}

export interface LegendAnnotation extends BasePageAnnotation {
  type: 'legend';
  title: string;
  entries: LegendEntry[];
  showCounts: boolean;
  showCategories: boolean;
  columns: 1 | 2;
}

/**
 * Manifest Box - Equipment and material quantities
 */
export interface ManifestEntry {
  item: string;
  quantity: number;
  unit: string;
  category?: string;
}

export interface ManifestAnnotation extends BasePageAnnotation {
  type: 'manifest';
  title: string;
  entries: ManifestEntry[];
  groupByCategory: boolean;
  showTotal: boolean;
}

/**
 * Notes/Callout - Text annotations
 */
export interface NotesAnnotation extends BasePageAnnotation {
  type: 'notes';
  title: string;
  content: string;
  fontSize: number;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  showBorder: boolean;
}

/**
 * Scale Bar - Map scale indicator
 */
export interface ScaleBarAnnotation extends BasePageAnnotation {
  type: 'scale-bar';
  scale: string;
  length: number;
  units: 'metric' | 'imperial';
  style: 'bar' | 'line' | 'alternating';
}

/**
 * Union type for all page annotations
 */
export type PageAnnotation = 
  | TitleBoxAnnotation 
  | NorthArrowAnnotation 
  | LegendAnnotation 
  | ManifestAnnotation 
  | NotesAnnotation
  | ScaleBarAnnotation;

/**
 * Annotation template - predefined or user-saved layouts
 */
export interface AnnotationTemplate {
  id: string;
  name: string;
  description?: string;
  isBuiltIn: boolean;
  annotations: PageAnnotation[];
  paperSize: 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
  orientation: 'portrait' | 'landscape';
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Annotation Editor state
 */
export interface AnnotationEditorState {
  annotations: PageAnnotation[];
  selectedId: string | null;
  activeTool: AnnotationType | null;
  capturedCanvas: string | null;
  paperSize: 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
  orientation: 'portrait' | 'landscape';
  zoom: number;
  panOffset: { x: number; y: number };
}

/**
 * Paper dimensions in pixels (at 96 DPI for screen)
 */
export const PAPER_DIMENSIONS: Record<string, { portrait: { width: number; height: number }; landscape: { width: number; height: number } }> = {
  'A4': { portrait: { width: 794, height: 1123 }, landscape: { width: 1123, height: 794 } },
  'A3': { portrait: { width: 1123, height: 1587 }, landscape: { width: 1587, height: 1123 } },
  'A2': { portrait: { width: 1587, height: 2245 }, landscape: { width: 2245, height: 1587 } },
  'A1': { portrait: { width: 2245, height: 3178 }, landscape: { width: 3178, height: 2245 } },
  'A0': { portrait: { width: 3178, height: 4494 }, landscape: { width: 4494, height: 3178 } },
};

/**
 * Default Title Box - matches existing TitleBoxData structure
 */
export const DEFAULT_TITLE_BOX: Omit<TitleBoxAnnotation, 'id'> = {
  type: 'title-box',
  position: { x: 0, y: 0 },
  width: 800,  // Will be overridden to full page width
  height: 120, // Will be overridden based on page height
  visible: true,
  useTitleBox: true,
  exportTitle: 'Traffic Guidance Scheme Export',
  tcpNumber: '',
  client: '',
  roadType: '',
  contact: '',
  phone: '',
  location: '',
  council: '',
  startDate: '',
  finishDate: '',
  startTime: '',
  finishTime: '',
  author: '',
  createdOn: new Date().toISOString().split('T')[0],
  modifiedBy: '',
  modifiedOn: new Date().toISOString().split('T')[0],
  state: 'SA',
  lengthOfZone: '',
  roadSpeed: '60',
  logos: { left: null, center: null, right: null },
  customSpacingRequirements: {},
  showLogoLeft: true,
  showLogoCenter: true,
  showLogoRight: true,
  showMetadataSection: true,
  showSpacingTable: true,
};

export const DEFAULT_NORTH_ARROW: Omit<NorthArrowAnnotation, 'id'> = {
  type: 'north-arrow',
  position: { x: 0, y: 0 },
  width: 60,
  height: 60,
  visible: true,
  rotation: 0,
  style: 'compass',
};

export const DEFAULT_LEGEND: Omit<LegendAnnotation, 'id'> = {
  type: 'legend',
  position: { x: 0, y: 0 },
  width: 200,
  height: 300,
  visible: true,
  title: 'LEGEND',
  entries: [],
  showCounts: true,
  showCategories: false,
  columns: 1,
};

export const DEFAULT_MANIFEST: Omit<ManifestAnnotation, 'id'> = {
  type: 'manifest',
  position: { x: 0, y: 0 },
  width: 220,
  height: 250,
  visible: true,
  title: 'EQUIPMENT',
  entries: [],
  groupByCategory: true,
  showTotal: true,
};

export const DEFAULT_NOTES: Omit<NotesAnnotation, 'id'> = {
  type: 'notes',
  position: { x: 0, y: 0 },
  width: 250,
  height: 150,
  visible: true,
  title: 'NOTES',
  content: '',
  fontSize: 12,
  backgroundColor: '#FFFFFF',
  borderColor: '#000000',
  textColor: '#000000',
  showBorder: true,
};

export const DEFAULT_SCALE_BAR: Omit<ScaleBarAnnotation, 'id'> = {
  type: 'scale-bar',
  position: { x: 0, y: 0 },
  width: 150,
  height: 30,
  visible: true,
  scale: '1:500',
  length: 100,
  units: 'metric',
  style: 'alternating',
};

/**
 * Built-in annotation templates
 */
export const BUILT_IN_TEMPLATES: AnnotationTemplate[] = [
  {
    id: 'standard-a3',
    name: 'Standard Engineering (A3)',
    description: 'Professional layout with title box, north arrow, legend, and scale bar',
    isBuiltIn: true,
    paperSize: 'A3',
    orientation: 'landscape',
    annotations: [
      {
        ...DEFAULT_TITLE_BOX,
        id: 'title-box-1',
        position: { x: 1587 - 450 - 20, y: 1123 - 320 - 20 },
      },
      {
        ...DEFAULT_NORTH_ARROW,
        id: 'north-arrow-1',
        position: { x: 1587 - 60 - 20, y: 20 },
      },
      {
        ...DEFAULT_LEGEND,
        id: 'legend-1',
        position: { x: 20, y: 1123 - 300 - 20 },
      },
      {
        ...DEFAULT_SCALE_BAR,
        id: 'scale-bar-1',
        position: { x: 20, y: 1123 - 300 - 20 - 50 },
      },
    ],
  },
  {
    id: 'minimal-a3',
    name: 'Minimal (A3)',
    description: 'Clean layout with just north arrow and scale bar',
    isBuiltIn: true,
    paperSize: 'A3',
    orientation: 'landscape',
    annotations: [
      {
        ...DEFAULT_NORTH_ARROW,
        id: 'north-arrow-1',
        position: { x: 1587 - 60 - 20, y: 20 },
      },
      {
        ...DEFAULT_SCALE_BAR,
        id: 'scale-bar-1',
        position: { x: 20, y: 1123 - 30 - 20 },
      },
    ],
  },
  {
    id: 'full-a3',
    name: 'Full Documentation (A3)',
    description: 'Complete layout with all annotation types',
    isBuiltIn: true,
    paperSize: 'A3',
    orientation: 'landscape',
    annotations: [
      {
        ...DEFAULT_TITLE_BOX,
        id: 'title-box-1',
        position: { x: 1587 - 450 - 20, y: 1123 - 320 - 20 },
      },
      {
        ...DEFAULT_NORTH_ARROW,
        id: 'north-arrow-1',
        position: { x: 1587 - 60 - 20, y: 20 },
      },
      {
        ...DEFAULT_LEGEND,
        id: 'legend-1',
        position: { x: 20, y: 20 },
      },
      {
        ...DEFAULT_MANIFEST,
        id: 'manifest-1',
        position: { x: 20, y: 340 },
      },
      {
        ...DEFAULT_SCALE_BAR,
        id: 'scale-bar-1',
        position: { x: 1587 - 450 - 20, y: 1123 - 320 - 20 - 50 },
      },
      {
        ...DEFAULT_NOTES,
        id: 'notes-1',
        position: { x: 20, y: 1123 - 150 - 20 },
      },
    ],
  },
];

/**
 * Generate unique ID for annotations
 */
export const generateAnnotationId = (): string => {
  return `ann-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Spacing requirements data for different states (from TitleBoxDialog)
 */
export const SPACING_REQUIREMENTS = {
  SA: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '300m +/-50m'],
    advanceWarning: ['D = 5', 'D = 15 (200m)', 'D = 45 (250m)', 'D = 70', 'D = speed'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['Recommended', 'Recommended', '20 - 30', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (5m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** SA Field Guide 2021 v9.1. For speeds ≥70km/h, "D" = speed of approaching traffic **'
  },
  NSW: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '250m +/-50m'],
    advanceWarning: ['D = 5 (50m)', 'D = 15 (180m)', 'D = 45 (240m)', 'D = 70', 'D = speed'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Recommended', '20 - 30', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (10m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** Estimates based on AS 1742 standards. For speeds ≥70km/h, "D" = speed of approaching traffic **'
  },
  VIC: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '200m +/-50m'],
    advanceWarning: ['D = 5 (50m)', 'D = 15 (175m)', 'D = 45 (230m)', 'D = 70', 'D = speed'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Recommended', '20 - 30', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (10m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** Estimates based on VicRoads guidelines. For speeds ≥70km/h, "D" = speed of approaching traffic **'
  },
  QLD: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '250m +/-50m'],
    advanceWarning: ['D = 5 (50m)', 'D = 15 (180m)', 'D = 45 (240m)', 'D = 70', 'D = speed'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Recommended', '20 - 30', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (10m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** Estimates based on TMR Queensland standards. For speeds ≥70km/h, "D" = speed of approaching traffic **'
  },
  WA: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '300m +/-50m'],
    advanceWarning: ['D = 5', 'D = 15', 'D = 45', 'D** = 70', 'D**'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Optional', 'Optional', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** For road speeds of 70km/h or more, "D" = speed of approaching traffic **'
  },
  TAS: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '200m +/-50m'],
    advanceWarning: ['D = 5', 'D = 15', 'D = 45', 'D** = 70', 'D**'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Optional', 'Optional', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** For road speeds of 70km/h or more, "D" = speed of approaching traffic **'
  },
  NT: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '300m +/-50m'],
    advanceWarning: ['D = 5', 'D = 15', 'D = 45', 'D** = 70', 'D**'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Optional', 'Optional', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** For road speeds of 70km/h or more, "D" = speed of approaching traffic **'
  },
  ACT: {
    speeds: [40, 50, 60, 70, '80-110'],
    bufferZone: ['n/a', 'n/a', 'n/a', 'n/a', '200m +/-50m'],
    advanceWarning: ['D = 5', 'D = 15', 'D = 45', 'D** = 70', 'D**'],
    taperArea: ['15', '15', '30', 'n/a', 'n/a'],
    safetyBuffer: ['15', 'Optional', 'Optional', '20 - 30', '20 - 30'],
    terminationArea: ['Optional (5m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)', 'Optional (15m)'],
    note: '** For road speeds of 70km/h or more, "D" = speed of approaching traffic **'
  }
};
